
import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, Search, FilterX } from 'lucide-react';
import type { TeacherMatchFilters as FiltersType } from '@/hooks/useTeacherMatch';

interface FilterOptions {
  subjects: string[];
  educationStages: string[];
  courseTypes: string[];
  specialized: string[];
  teachingStyles: string[];
  styleLevels: number[];
  gender: string[];
  personality: string[];
  trialLesson: string[];
  branches: string[];
  studentProficiency: string[];
}

interface Props {
  filters: FiltersType;
  filterOptions: FilterOptions | null;
  onUpdateFilters: (updates: Partial<FiltersType>) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  resultCount: number;
  totalCount: number;
}

interface MultiSelectFilterProps {
  label: string;
  options: string[];
  selected: string[];
  onChange: (values: string[]) => void;
}

const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({ label, options, selected, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full min-w-[140px] px-3 py-2 text-sm border rounded-md bg-background hover:bg-accent text-left flex items-center justify-between"
      >
        <span className={selected.length ? 'text-foreground' : 'text-muted-foreground'}>
          {selected.length ? `${label} (${selected.length})` : label}
        </span>
        <svg className="h-4 w-4 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute z-50 mt-1 w-full min-w-[200px] bg-popover border rounded-md shadow-lg max-h-60 overflow-y-auto">
            {options.map(opt => (
              <label
                key={opt}
                className="flex items-center gap-2 px-3 py-2 hover:bg-accent cursor-pointer text-sm"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(opt)}
                  onChange={(e) => {
                    if (e.target.checked) onChange([...selected, opt]);
                    else onChange(selected.filter(s => s !== opt));
                  }}
                  className="rounded border-input"
                />
                {opt}
              </label>
            ))}
            {options.length === 0 && (
              <div className="px-3 py-2 text-sm text-muted-foreground">ไม่มีข้อมูล</div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/** Inline checkbox group for small option sets (≤5 items) */
const CheckboxGroupFilter: React.FC<{
  label: string;
  options: string[];
  selected: string[];
  onChange: (values: string[]) => void;
}> = ({ label, options, selected, onChange }) => {
  const toggle = (opt: string) => {
    if (selected.includes(opt)) onChange(selected.filter(s => s !== opt));
    else onChange([...selected, opt]);
  };

  return (
    <div className="space-y-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map(opt => (
          <label
            key={opt}
            className="flex items-center gap-1.5 cursor-pointer text-sm"
          >
            <Checkbox
              checked={selected.includes(opt)}
              onCheckedChange={() => toggle(opt)}
            />
            <span className={selected.includes(opt) ? 'text-foreground font-medium' : 'text-muted-foreground'}>
              {opt}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};

/** Inline checkbox group for number arrays */
const NumberCheckboxGroupFilter: React.FC<{
  label: string;
  options: number[];
  selected: number[];
  onChange: (values: number[]) => void;
  renderLabel?: (v: number) => string;
}> = ({ label, options, selected, onChange, renderLabel }) => {
  const toggle = (opt: number) => {
    if (selected.includes(opt)) onChange(selected.filter(s => s !== opt));
    else onChange([...selected, opt]);
  };

  return (
    <div className="space-y-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map(opt => (
          <label
            key={opt}
            className="flex items-center gap-1.5 cursor-pointer text-sm"
          >
            <Checkbox
              checked={selected.includes(opt)}
              onCheckedChange={() => toggle(opt)}
            />
            <span className={selected.includes(opt) ? 'text-foreground font-medium' : 'text-muted-foreground'}>
              {renderLabel ? renderLabel(opt) : String(opt)}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};

export const TeacherMatchFilters: React.FC<Props> = ({
  filters,
  filterOptions,
  onUpdateFilters,
  onClearFilters,
  hasActiveFilters,
  resultCount,
  totalCount,
}) => {
  const [searchInput, setSearchInput] = useState(filters.search);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        onUpdateFilters({ search: searchInput });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Sync external changes
  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);

  if (!filterOptions) return null;

  // Collect active filter tags
  const tags: Array<{ key: string; label: string; value: string; onRemove: () => void }> = [];
  
  const addTags = (key: string, label: string, values: string[], filterKey: keyof FiltersType) => {
    values.forEach(v => {
      tags.push({
        key: `${key}-${v}`,
        label,
        value: v,
        onRemove: () => onUpdateFilters({ [filterKey]: (filters[filterKey] as string[]).filter(x => x !== v) }),
      });
    });
  };

  if (filters.search) tags.push({ key: 'search', label: 'ค้นหา', value: filters.search, onRemove: () => { onUpdateFilters({ search: '' }); setSearchInput(''); } });
  addTags('subject', 'วิชา', filters.subjects, 'subjects');
  addTags('stage', 'ระดับ', filters.educationStages, 'educationStages');
  addTags('course', 'ประเภท', filters.courseTypes, 'courseTypes');
  addTags('spec', 'เฉพาะทาง', filters.specialized, 'specialized');
  addTags('style', 'สไตล์', filters.teachingStyles, 'teachingStyles');
  filters.styleLevels.forEach(v => {
    tags.push({
      key: `styleLevel-${v}`,
      label: 'ระดับสไตล์',
      value: `Lv.${v}`,
      onRemove: () => onUpdateFilters({ styleLevels: filters.styleLevels.filter(x => x !== v) }),
    });
  });
  addTags('gender', 'เพศ', filters.gender, 'gender');
  addTags('pers', 'บุคลิกภาพ', filters.personality, 'personality');
  addTags('trial', 'ทดลองสอน', filters.trialLesson, 'trialLesson');
  addTags('branch', 'สาขา', filters.branches, 'branches');
  addTags('prof', 'ระดับนักเรียน', filters.studentProficiency, 'studentProficiency');
  if (filters.status !== 'ACTIVE') tags.push({ key: 'status', label: 'สถานะ', value: filters.status, onRemove: () => onUpdateFilters({ status: 'ACTIVE' }) });

  return (
    <div className="space-y-4">
      {/* Search + Status row */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="ค้นหาชื่อ, รหัส, ชื่อเล่น..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={filters.status} onValueChange={v => onUpdateFilters({ status: v })}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="สถานะ" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ACTIVE">Active</SelectItem>
            <SelectItem value="-">Inactive</SelectItem>
            <SelectItem value="all">ทั้งหมด</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Dropdown filters (for large option sets) */}
      <div className="flex flex-wrap gap-2">
        <MultiSelectFilter label="วิชา" options={filterOptions.subjects} selected={filters.subjects} onChange={v => onUpdateFilters({ subjects: v })} />
        <MultiSelectFilter label="ระดับชั้น" options={filterOptions.educationStages} selected={filters.educationStages} onChange={v => onUpdateFilters({ educationStages: v })} />
        <MultiSelectFilter label="เฉพาะทาง" options={filterOptions.specialized} selected={filters.specialized} onChange={v => onUpdateFilters({ specialized: v })} />
        <MultiSelectFilter label="สไตล์การสอน" options={filterOptions.teachingStyles} selected={filters.teachingStyles} onChange={v => onUpdateFilters({ teachingStyles: v })} />
        <MultiSelectFilter label="บุคลิกภาพ (MBTI)" options={filterOptions.personality} selected={filters.personality} onChange={v => onUpdateFilters({ personality: v })} />
        <MultiSelectFilter label="สาขา" options={filterOptions.branches} selected={filters.branches} onChange={v => onUpdateFilters({ branches: v })} />
        <MultiSelectFilter label="ระดับนักเรียน" options={filterOptions.studentProficiency} selected={filters.studentProficiency} onChange={v => onUpdateFilters({ studentProficiency: v })} />
      </div>

      {/* Inline checkbox filters (for small option sets) */}
      <div className="flex flex-wrap gap-6 p-3 bg-muted/30 rounded-lg border">
        <CheckboxGroupFilter
          label="เพศ"
          options={filterOptions.gender}
          selected={filters.gender}
          onChange={v => onUpdateFilters({ gender: v })}
        />
        <CheckboxGroupFilter
          label="Regular/Inter"
          options={filterOptions.courseTypes}
          selected={filters.courseTypes}
          onChange={v => onUpdateFilters({ courseTypes: v })}
        />
        <CheckboxGroupFilter
          label="ทดลองสอน"
          options={filterOptions.trialLesson}
          selected={filters.trialLesson}
          onChange={v => onUpdateFilters({ trialLesson: v })}
        />
        <NumberCheckboxGroupFilter
          label="ระดับสไตล์การสอน"
          options={filterOptions.styleLevels}
          selected={filters.styleLevels}
          onChange={v => onUpdateFilters({ styleLevels: v })}
          renderLabel={v => `Lv.${v}`}
        />
      </div>

      {/* Active filter tags */}
      {tags.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">ตัวกรอง:</span>
          {tags.map(tag => (
            <Badge key={tag.key} variant="secondary" className="gap-1 pr-1">
              <span className="text-xs text-muted-foreground">{tag.label}:</span>
              <span className="text-xs font-medium">{tag.value}</span>
              <button onClick={tag.onRemove} className="ml-0.5 hover:bg-muted rounded-full p-0.5">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          <Button variant="ghost" size="sm" onClick={() => { onClearFilters(); setSearchInput(''); }} className="text-xs h-7">
            <FilterX className="h-3 w-3 mr-1" />
            ล้างทั้งหมด
          </Button>
        </div>
      )}

      {/* Result count */}
      <div className="text-sm text-muted-foreground">
        แสดง <span className="font-semibold text-foreground">{resultCount}</span> จาก {totalCount} ครู
      </div>
    </div>
  );
};
